/**
 * Notification Sound System
 * Provides audio feedback for different notification types
 */

let audioContext: AudioContext | null = null;

const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a notification sound using Web Audio API with volume control
 * This creates a pleasant, non-intrusive notification tone
 */
export const playNotificationSound = (
  type: 'connection_request' | 'connection_accepted' | 'default' = 'default',
  volumeOverride?: number
) => {
  try {
    const context = getAudioContext();
    
    // Get volume from preferences or use override/default
    const volume = volumeOverride !== undefined ? volumeOverride : getStoredVolume();
    
    // Create oscillators for a pleasant two-tone sound
    const oscillator1 = context.createOscillator();
    const oscillator2 = context.createOscillator();
    const gainNode = context.createGain();
    
    // Connect audio nodes
    oscillator1.connect(gainNode);
    oscillator2.connect(gainNode);
    gainNode.connect(context.destination);
    
    // Configure sound based on notification type
    const soundConfig = getSoundConfig(type);
    
    oscillator1.type = 'sine';
    oscillator2.type = 'sine';
    oscillator1.frequency.setValueAtTime(soundConfig.freq1, context.currentTime);
    oscillator2.frequency.setValueAtTime(soundConfig.freq2, context.currentTime);
    
    // Apply volume from preferences (convert 0-100 to 0-0.3 range)
    const adjustedVolume = (volume / 100) * soundConfig.volume;
    
    // Create envelope for smooth attack and release
    gainNode.gain.setValueAtTime(0, context.currentTime);
    gainNode.gain.linearRampToValueAtTime(adjustedVolume, context.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, context.currentTime + soundConfig.duration);
    
    // Start and stop oscillators
    const now = context.currentTime;
    oscillator1.start(now);
    oscillator2.start(now);
    oscillator1.stop(now + soundConfig.duration);
    oscillator2.stop(now + soundConfig.duration);
    
    console.log('Notification sound played:', type, 'volume:', volume);
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};

/**
 * Get sound configuration for different notification types
 */
const getSoundConfig = (type: string) => {
  switch (type) {
    case 'connection_request':
      return {
        freq1: 523.25, // C5
        freq2: 659.25, // E5
        duration: 0.3,
        volume: 0.15
      };
    case 'connection_accepted':
      return {
        freq1: 659.25, // E5
        freq2: 783.99, // G5
        duration: 0.4,
        volume: 0.2
      };
    default:
      return {
        freq1: 440, // A4
        freq2: 554.37, // C#5
        duration: 0.25,
        volume: 0.12
      };
  }
};

/**
 * Get stored volume from user preferences
 */
const getStoredVolume = (): number => {
  // This would normally fetch from the database preferences
  // For now, return default of 50%
  return 50;
};

/**
 * Check if sounds should be played based on user preferences and quiet hours
 */
export const shouldPlaySound = async (userId?: string): Promise<boolean> => {
  // Check if user has muted notifications in browser
  if (document.hidden) {
    return false; // Don't play sound if tab is not visible
  }
  
  // If userId is provided, check database preferences
  if (userId) {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: preferences } = await supabase
        .from("user_notification_preferences")
        .select("sound_enabled, quiet_hours_enabled, quiet_hours_start, quiet_hours_end")
        .eq("user_id", userId)
        .single();

      if (preferences) {
        // Check if sounds are enabled
        if (!preferences.sound_enabled) {
          return false;
        }

        // Check quiet hours
        if (preferences.quiet_hours_enabled) {
          const now = new Date();
          const currentTime = now.toTimeString().substring(0, 5); // HH:MM format
          const startTime = preferences.quiet_hours_start?.substring(0, 5) || "22:00";
          const endTime = preferences.quiet_hours_end?.substring(0, 5) || "08:00";

          // Handle quiet hours that span midnight
          if (startTime > endTime) {
            // e.g., 22:00 to 08:00
            if (currentTime >= startTime || currentTime < endTime) {
              return false;
            }
          } else {
            // e.g., 01:00 to 06:00
            if (currentTime >= startTime && currentTime < endTime) {
              return false;
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking notification preferences:", error);
    }
  }
  
  // Check localStorage fallback
  const soundsEnabled = localStorage.getItem('notification_sounds_enabled');
  if (soundsEnabled === 'false') {
    return false;
  }
  
  return true;
};

/**
 * Play sound with automatic checks including user preferences
 */
export const playNotificationSoundSafe = async (
  type: 'connection_request' | 'connection_accepted' | 'default' = 'default',
  userId?: string
) => {
  if (await shouldPlaySound(userId)) {
    playNotificationSound(type);
  }
};
